import { useEffect } from "react";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { Route as RootRoute } from "../../__root";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import {
  useMicrogroupCompleteOnboarding,
  useMicrogroupOnboardingToken,
} from "../../../hooks/useMicrogroupQueries";
import { MicrogroupOnboardingStepper } from "../../../components/tenant/MicrogroupOnboardingStepper";
import { ApiError } from "../../../lib/apiClient";
import { hydrateAuth } from "../../../lib/authStore";

/**
 * UC-006 magic link landing — `/p/microgroup/{token}` (Phase 28 / Milestone 20.3).
 *
 * Token validation flow:
 *   1. `GET /api/public/microgroup/consume-token/{token}` — idempotent
 *      (nechcizovat ConsumedToken yet, jen check existence + expiraci).
 *   2. Pokud 410 EXPIRED_TOKEN nebo 404 → render 410 page "Odkaz již použit
 *      nebo expiroval".
 *   3. Pokud 200 → render `MicrogroupOnboardingStepper` se 5-step wizardem.
 *   4. Final submit (Krok 4 Accept) volá
 *      `POST /api/public/microgroup/onboarding/{token}/complete`, který
 *      consume token + INSERT users + UPDATE microgroup_member + JWT issue.
 *   5. Po success: re-hydrate auth + redirect na `/tenant/dashboard`.
 */

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/p/microgroup/$token",
  component: MicrogroupOnboardingLanding,
});

function MicrogroupOnboardingLanding() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const tokenQuery = useMicrogroupOnboardingToken(token);
  const completeMutation = useMicrogroupCompleteOnboarding();

  // Defensive: pokud token state se změní na expired po consume race, redirect.
  useEffect(() => {
    if (
      tokenQuery.isError &&
      tokenQuery.error instanceof ApiError &&
      (tokenQuery.error.status === 410 ||
        tokenQuery.error.errorCode === "EXPIRED_TOKEN")
    ) {
      // No-op; render 410 inline below.
    }
  }, [tokenQuery.isError, tokenQuery.error]);

  if (tokenQuery.isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-secondary px-4 py-12">
        <LoadingIndicator label="Ověřuji odkaz…" />
      </main>
    );
  }

  if (tokenQuery.isError) {
    const isExpired =
      tokenQuery.error instanceof ApiError &&
      (tokenQuery.error.status === 410 ||
        tokenQuery.error.status === 404 ||
        tokenQuery.error.errorCode === "EXPIRED_TOKEN" ||
        tokenQuery.error.errorCode === "TOKEN_NOT_FOUND");
    return (
      <main
        className="min-h-screen bg-secondary px-4 py-12 md:py-16"
        aria-labelledby="onb-410-heading"
      >
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-5">
          <h1 id="onb-410-heading" className="text-xl font-semibold text-primary md:text-display-xs">
            {isExpired ? "Odkaz již nelze použít" : "Ověření odkazu selhalo"}
          </h1>
          <InlineAlert
            variant="warning"
            title={isExpired ? "Odkaz vypršel nebo byl použit" : "Chyba sítě"}
          >
            {isExpired
              ? "Pozvánka vypršela nebo už byla použita. Požádejte sub-admina o novou pozvánku."
              : "Zkuste odkaz otevřít znovu. Pokud problém přetrvává, kontaktujte podporu."}
          </InlineAlert>
        </div>
      </main>
    );
  }

  if (!tokenQuery.data) return null;

  return (
    <main className="min-h-screen bg-secondary px-4 py-12 md:py-16">
      <div className="mx-auto w-full max-w-2xl">
      <MicrogroupOnboardingStepper
        tokenInfo={tokenQuery.data}
        onSubmit={async (input) => {
          await completeMutation.mutateAsync({
            token,
            password: input.password,
            consent_text_version: input.consent_text_version,
            consent_types: input.consent_types,
          });
        }}
        onCompleted={async () => {
          // Re-hydrate authStore (backend has set HttpOnly cookie via Set-Cookie).
          await hydrateAuth();
          // Mírná pauza pro user feedback na Success kroku, pak redirect.
          setTimeout(() => {
            navigate({ to: "/tenant/dashboard" });
          }, 1500);
        }}
      />
      </div>
    </main>
  );
}
